import { api } from "@/common/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AccountRole } from "./accountsApi";

export type MemberWithUser = {
  id: string;
  role: AccountRole;
  status: "active" | "suspended";
  createdAt: string;
  user: { id: string; email: string };
};

export function useMembersList(accountId: string) {
  return useQuery<MemberWithUser[]>({
    queryKey: ["accounts", accountId, "memberships"],
    queryFn: () => api.get<MemberWithUser[]>(`/accounts/${accountId}/memberships`),
    enabled: !!accountId,
  });
}

export function useChangeRole(accountId: string) {
  const qc = useQueryClient();
  return useMutation<MemberWithUser, Error, { membershipId: string; role: AccountRole }>({
    mutationFn: ({ membershipId, role }) =>
      api.patch<MemberWithUser>(`/accounts/${accountId}/memberships/${membershipId}`, { role }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts", accountId, "memberships"] });
    },
  });
}

export function useRemoveMember(accountId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (membershipId) =>
      api.delete<void>(`/accounts/${accountId}/memberships/${membershipId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts", accountId, "memberships"] });
    },
  });
}
